import {useRef, useEffect, useState} from 'react';
import {createPortal} from 'react-dom';
import PropTypes from 'prop-types';
import {StyleSheetManager} from 'styled-components';
import ShadowRootContext from '../../contexts/ShadowRootContext';

// Import all styles as inline strings for shadow DOM injection
import appStyles from '../../App.scss?inline';

/**
 * ShadowDomWrapper - Wraps children in a Shadow DOM for style isolation.
 * This prevents host app styles from affecting the microfrontend and vice versa.
 *
 * Fonts are injected into the main document since they're document-scoped.
 * Uses StyleSheetManager to redirect styled-components into the shadow root.
 */
const ShadowDomWrapper = ({children}) => {
  const hostRef = useRef(null);
  const [shadowRoot, setShadowRoot] = useState(null);
  const [styleTarget, setStyleTarget] = useState(null);

  useEffect(() => {
    if (hostRef.current && !hostRef.current.shadowRoot) {
      // Create shadow root
      const shadow = hostRef.current.attachShadow({mode: 'open'});

      // Create a style element for styled-components to inject into
      const styledComponentsTarget = document.createElement('style');
      styledComponentsTarget.id = 'styled-components-target';
      shadow.appendChild(styledComponentsTarget);

      // Create and inject styles including :host rules
      // PostCSS converts rem to px at build time, so no runtime conversion needed
      const hostStyles = `
        :host {
          display: block;
          width: 100%;
          font-size: 16px;
        }
        .heatshrink-shadow-root {
          font-size: 16px;
        }
      `;
      const styleSheet = new CSSStyleSheet();
      styleSheet.replaceSync(hostStyles + appStyles);
      shadow.adoptedStyleSheets = [styleSheet];

      // Create a container div for React to render into
      const container = document.createElement('div');
      container.className = 'heatshrink-shadow-root';
      container.style.fontSize = '16px';
      container.style.display = 'block';
      container.style.width = '100%';
      shadow.appendChild(container);

      // eslint-disable-next-line react-hooks/set-state-in-effect -- Shadow DOM setup requires synchronous state updates to create portal target
      setStyleTarget(styledComponentsTarget);
       
      setShadowRoot(container);
    }
  }, []);

  // Inject font links into document head (fonts are document-scoped, not shadow-scoped)
  useEffect(() => {
    const fontUrls = [
      'https://fonts.googleapis.com/css2?family=Oswald:wght@200;300;400;500;600;700&display=swap',
      'https://fonts.googleapis.com/css2?family=Open+Sans:ital,wght@0,300;0,400;0,600;0,700;0,800;1,300;1,400;1,600;1,700;1,800&display=swap',
      'https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap',
      'https://fonts.googleapis.com/css2?family=Raleway:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap',
      'https://fonts.googleapis.com/css?family=Roboto+Slab',
      'https://fonts.googleapis.com/css?family=Bebas+Neue',
      'https://fonts.googleapis.com/css?family=Comfortaa',
      'https://fonts.googleapis.com/css?family=Alfa+Slab+One',
      'https://fonts.googleapis.com/css?family=Pacifico',
      'https://fonts.googleapis.com/css?family=Roboto+Mono',
      'https://fonts.googleapis.com/css?family=Courier+Prime',
      'https://fonts.googleapis.com/css?family=Yellowtail'
    ];

    const addedLinks = [];

    fontUrls.forEach((url) => {
      // Check if already loaded
      const existing = document.querySelector(`link[href="${url}"]`);
      if (!existing) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = url;
        link.dataset.hspFont = 'true';
        document.head.appendChild(link);
        addedLinks.push(link);
      }
    });

    // Cleanup on unmount (optional - fonts may be wanted globally)
    return () => {
      addedLinks.forEach((link) => link.remove());
    };
  }, []);

  return (
    <>
      <div 
        ref={hostRef} 
        className="heatshrink-editor-host"
        style={{display: 'block', width: '100%'}}
      />
      {shadowRoot && styleTarget && createPortal(
        <StyleSheetManager target={styleTarget}>
          <ShadowRootContext.Provider value={shadowRoot}>
            {children}
          </ShadowRootContext.Provider>
        </StyleSheetManager>,
        shadowRoot
      )}
    </>
  );
};

ShadowDomWrapper.propTypes = {
  children: PropTypes.node.isRequired
};

export default ShadowDomWrapper;
